import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createTableNode, $createTableRowNode, INSERT_TABLE_COMMAND } from "@lexical/table";
import { mergeRegister } from "@lexical/utils";
import { $getNodeByKey, $getSelection, $isRangeSelection, COMMAND_PRIORITY_EDITOR } from "lexical";
import { type FC, useEffect } from "react";
import {
  $createStylableTableCellMui,
  $isStylableTableCellMui,
} from "../nodes/stylable-table-cell-mui";
import {
  INSERT_STYLEABLE_TABLE_CELL_COMMAND,
  INSERT_STYLEABLE_TABLE_COMMAND,
  SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND,
} from "../types/commands";
import { $isInTableNode } from "../utils/is-in-table-node";
import { type StyleableColumnConfig, useStyleableColumns } from "../utils/use-styleable-columns";
import { useLexicalTable } from "./use-lexical-table";

export const SingleTablePlugin: FC<{ styleableColumnConfig?: StyleableColumnConfig | null }> = ({
  styleableColumnConfig,
}) => {
  const [editor] = useLexicalComposerContext();
  const stylableColumns = useStyleableColumns();

  const { createTableNode, insertColumn } = useLexicalTable();

  useEffect(() => {
    // This will initially register to atleast block unallowed table insertions
    // but will be overridden by the stylable columns command if enabled
    if (stylableColumns.enabled) {
      return;
    }
    return editor.registerCommand(
      INSERT_TABLE_COMMAND,
      () => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return false;

        return $isInTableNode(selection.anchor.getNode());
      },
      1,
    );
  }, [editor, stylableColumns]);

  useEffect(() => {
    if (!stylableColumns.enabled) {
      return;
    }

    return mergeRegister(
      editor.registerCommand(
        INSERT_STYLEABLE_TABLE_COMMAND,
        // default to 50/50 if no column proportions are provided which is the same as current behaviour.
        ({ columnProportions = [50, 50] }) => {
          const tableNode = createTableNode(
            { columns: columnProportions.length, rows: 1 },
            {
              createCell: () => $createStylableTableCellMui(styleableColumnConfig),
              createRow: () => $createTableRowNode(),
              createTable: () => $createTableNode(),
            },
          );

          if (!tableNode) return false;

          editor.update(() => {
            tableNode.setColWidths(columnProportions);
          });

          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand(
        INSERT_STYLEABLE_TABLE_CELL_COMMAND,
        ({ insertAfter }) => {
          insertColumn(insertAfter, {
            createCell: () => $createStylableTableCellMui(styleableColumnConfig),
          });
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand(
        SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND,
        ({ nodeKey, ...paddings }) => {
          editor.update(
            () => {
              const tableCellNode = $getNodeByKey(nodeKey);

              if (!$isStylableTableCellMui(tableCellNode)) {
                return false;
              }

              if (!paddings.individualPadding) {
                tableCellNode.setCommonPadding(paddings.common);
              }
              if (paddings.individualPadding) {
                tableCellNode.setIndividualPadding(paddings);
              }
            },
            {
              discrete: true,
            },
          );
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    );
  }, [editor, stylableColumns, styleableColumnConfig, createTableNode, insertColumn]);

  return null;
};
